import React, { Suspense, useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import {
  Alert as MUIAlert,
  Box,
  Button,
  ButtonBase,
  Chip,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  Check,
  DragIndicator,
  Edit,
  MoreHoriz,
  NotificationsActive,
  PhoneInTalk,
  Replay,
  Schedule,
} from '@mui/icons-material'
import { Redirect } from 'wouter'
import AppLink from '../util/AppLink'
import { UserAvatar } from '../util/avatars'
import { DestinationAvatar } from '../util/DestinationAvatar'
import { QuerySetFavoriteButton } from '../util/QuerySetFavoriteButton'
import { GenericError, ObjectNotFound } from '../error-pages'
import PolicyDeleteDialog from '../escalation-policies/PolicyDeleteDialog'
import StepDialog from './StepDialog'
import { PolicyDialog, RepeatChoice } from './EditDialogs'
import PolicyStepDeleteDialog from '../escalation-policies/PolicyStepDeleteDialog'
import { actionLabel } from './ListPages'
import { SeverityChip } from './AlertsPage'
import { Empty, IconTile, Page, Panel, Row, useBrand } from './ui'
import { stepChannelsApi } from './api'
import { orderSteps } from './BotAdmin'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsPolicy($id: ID!) {
    escalationPolicy(id: $id) {
      id
      name
      description
      repeat
      steps {
        id
        stepNumber
        delayMinutes
        actions {
          type
          args
          displayInfo {
            ... on DestinationDisplayInfo {
              text
              iconURL
              iconAltText
              linkURL
            }
            ... on DestinationDisplayInfoError {
              error
            }
          }
        }
      }
      assignedTo {
        id
        name
        type
      }
    }
  }
`

const update = gql`
  mutation ErecordsUpdatePolicy($input: UpdateEscalationPolicyInput!) {
    updateEscalationPolicy(input: $input)
  }
`

type Action = {
  type: string
  args: Record<string, string>
  displayInfo: {
    text?: string
    iconURL?: string
    iconAltText?: string
    linkURL?: string
    error?: string
  }
}
type Step = {
  id: string
  stepNumber: number
  delayMinutes: number
  actions: Action[]
}

function Target({ a }: { a: Action }): React.JSX.Element {
  const c = useBrand()
  const isUser = a.type === 'builtin-user'
  const to = isUser
    ? `/users/${a.args.user_id}`
    : a.type === 'builtin-schedule'
      ? `/schedules/${a.args.schedule_id}`
      : undefined
  const chip = (
    <Chip
      variant='outlined'
      clickable={Boolean(to || a.displayInfo.linkURL)}
      avatar={
        isUser ? (
          <UserAvatar userID={a.args.user_id} />
        ) : (
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              '& .MuiAvatar-root': { width: 24, height: 24 },
            }}
          >
            <DestinationAvatar
              iconURL={a.displayInfo.iconURL}
              iconAltText={a.displayInfo.iconAltText}
              error={Boolean(a.displayInfo.error)}
            />
          </Box>
        )
      }
      label={
        <span>
          {actionLabel(a)}
          {a.type === 'builtin-schedule' && (
            <span style={{ color: c.muted }}> · whoever is on call</span>
          )}
        </span>
      }
      sx={{ ml: '0 !important' }}
    />
  )
  if (to) {
    return (
      <AppLink to={to} underline='none'>
        {chip}
      </AppLink>
    )
  }
  // e.g. a Microsoft Teams channel: opens the channel in Teams
  if (a.displayInfo.linkURL) {
    return (
      <Link
        href={a.displayInfo.linkURL}
        target='_blank'
        rel='noopener noreferrer'
        underline='none'
        title='Open in Microsoft Teams'
      >
        {chip}
      </Link>
    )
  }
  return chip
}

// StepCard is one step of the timeline: number, when it starts, who it
// notifies, Edit, and a menu for moving or deleting it.
function StepCard(props: {
  n: number
  heading: string
  sends?: string[] // what the step sends right away, when it picks
  actions: Action[]
  first: boolean
  last: boolean
  busy: boolean
  onEdit: () => void
  onMove: (d: number) => void
  onDelete: () => void
  grip?: React.ReactNode // drag handle, when there is more than one step
}): React.JSX.Element {
  const c = useBrand()
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const pick = (fn: () => void) => (): void => {
    setMenu(null)
    fn()
  }
  return (
    <Box
      sx={{
        border: `1px solid ${c.line}`,
        borderRadius: '14px',
        p: 2,
        bgcolor: c.surface,
        transition: 'border-color 120ms',
        '&:hover': { borderColor: c.muted },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {props.grip}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 13,
            bgcolor: c.ink,
            color: c.surface,
            flexShrink: 0,
          }}
        >
          {props.n}
        </Box>
        <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {props.heading}
        </Typography>
        <Button size='small' startIcon={<Edit />} onClick={props.onEdit}>
          Edit
        </Button>
        <IconButton
          size='small'
          aria-label={`More for step ${props.n}`}
          onClick={(e) => setMenu(e.currentTarget)}
        >
          <MoreHoriz fontSize='small' />
        </IconButton>
        <Menu
          anchorEl={menu}
          open={Boolean(menu)}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            disabled={props.first || props.busy}
            onClick={pick(() => props.onMove(-1))}
          >
            <ListItemIcon>
              <ArrowUpward fontSize='small' />
            </ListItemIcon>
            Move up
          </MenuItem>
          <MenuItem
            disabled={props.last || props.busy}
            onClick={pick(() => props.onMove(1))}
          >
            <ListItemIcon>
              <ArrowDownward fontSize='small' />
            </ListItemIcon>
            Move down
          </MenuItem>
          <MenuItem onClick={pick(props.onDelete)} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Delete fontSize='small' />
            </ListItemIcon>
            Delete step
          </MenuItem>
        </Menu>
      </Box>
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25, pl: 5.5 }}
      >
        {props.actions.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: c.warn }}>
            Nobody on this step yet. Edit it to add people or a Teams channel.
          </Typography>
        ) : (
          props.actions.map((a, k) => <Target key={k} a={a} />)
        )}
      </Box>
      {props.sends && props.sends.length > 0 && (
        <Typography sx={{ fontSize: 12, color: c.muted, mt: 1, pl: 5.5 }}>
          Sends right away:{' '}
          {orderSteps
            .filter((w) => props.sends?.includes(w.key))
            .map((w) => w.label)
            .join(', ')}
        </Typography>
      )}
    </Box>
  )
}

// Connector is the line between two timeline items, with an optional note
// ("Wait 15 min for an acknowledgement").
function Connector(props: { children?: React.ReactNode }): React.JSX.Element {
  const c = useBrand()
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', minHeight: 40, pl: '29px' }}
    >
      <Box
        aria-hidden
        sx={{ width: 2, alignSelf: 'stretch', bgcolor: c.line, flexShrink: 0 }}
      />
      {props.children && (
        <Box
          sx={{
            ml: 2.5,
            my: 0.75,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            bgcolor: c.soft,
            fontSize: 13,
            color: c.muted,
            '& svg': { fontSize: 16 },
          }}
        >
          {props.children}
        </Box>
      )}
    </Box>
  )
}

// sentence explains the whole policy in one line, in plain words.
export function sentence(steps: Step[], repeat: number): string {
  if (!steps.length)
    return 'No steps yet: alerts on services using this policy notify nobody.'
  let at = 0
  const parts = steps.map((st, i) => {
    const who = st.actions.map(actionLabel).join(' and ') || 'nobody'
    const s =
      i === 0
        ? `notify ${who} right away`
        : `if nobody acknowledged after ${at} min, notify ${who}`
    at += st.delayMinutes
    return s
  })
  const end =
    repeat > 0
      ? `Then start over (${repeat} more time${repeat > 1 ? 's' : ''}).`
      : 'Then stop.'
  return `When an alert comes in: ${parts.join('; ')}. ${end}`
}

// PolicyDetail replaces upstream's escalation policy page: the policy as a
// plain sentence, then a timeline (step cards, the wait between them, "Add
// step", and what happens after the last step), and the services using it.
export default function PolicyDetail(props: {
  policyID: string
}): React.JSX.Element {
  const c = useBrand()
  const [q, refetch] = useQuery({
    query,
    variables: { id: props.policyID },
    context: noSuspense,
  })
  const [upd, commit] = useMutation(update)
  const [edit, setEdit] = useState(false)
  const [del, setDel] = useState(false)
  const [addStep, setAddStep] = useState(false)
  const [editStep, setEditStep] = useState<string>()
  const [delStep, setDelStep] = useState<string>()
  const [gripped, setGripped] = useState<string | null>(null)
  const [drag, setDrag] = useState<number | null>(null)
  const [over, setOver] = useState<{ i: number; after: boolean } | null>(null)
  const [sends, setSends] = useState<Record<string, string[]>>({})
  const loadSends = (): void => {
    stepChannelsApi
      .forPolicy(props.policyID)
      .then(setSends)
      .catch(() => setSends({}))
  }
  useEffect(loadSends, [props.policyID])

  const p = q.data?.escalationPolicy
  if (q.error) return <GenericError error={q.error.message} />
  if (!p) {
    if (del) return <Redirect to='/escalation-policies' />
    return q.fetching ? (
      <Typography sx={{ p: 3, color: c.muted }}>Loading…</Typography>
    ) : (
      <ObjectNotFound />
    )
  }
  const steps: Step[] = p.steps
  const reload = (): void => {
    refetch({ requestPolicy: 'network-only' })
    loadSends()
  }
  const save = (input: Record<string, unknown>): void => {
    commit(
      { input: { id: p.id, ...input } },
      { additionalTypenames: ['EscalationPolicy'] },
    ).then(reload)
  }
  const reorder = (from: number, to: number): void => {
    if (from === to) return
    const ids = steps.map((s) => s.id)
    const [x] = ids.splice(from, 1)
    ids.splice(to, 0, x)
    save({ stepIDs: ids })
  }
  const move = (i: number, d: number): void => reorder(i, i + d)
  // drag and drop: hold the grip, drag the step, a line shows where it lands
  const drop = (): void => {
    if (drag !== null && over) {
      let to = over.after ? over.i + 1 : over.i
      if (to > drag) to -= 1
      reorder(drag, to)
    }
    setDrag(null)
    setOver(null)
    setGripped(null)
  }
  const callsPeople = steps.some((st) =>
    st.actions.some((a) =>
      ['builtin-user', 'builtin-schedule'].includes(a.type),
    ),
  )
  const services = (
    p.assignedTo as { id: string; name: string; type: string }[]
  ).filter((t) => t.type === 'service')
  let at = 0

  return (
    <Page
      title={p.name}
      subtitle={p.description}
      maxWidth={1000}
      actions={
        <React.Fragment>
          <QuerySetFavoriteButton id={p.id} type='escalationPolicy' />
          <Button
            variant='outlined'
            startIcon={<Edit />}
            onClick={() => setEdit(true)}
          >
            Edit
          </Button>
        </React.Fragment>
      }
    >
      <Suspense>
        {edit && (
          <PolicyDialog
            policyID={p.id}
            onClose={() => {
              setEdit(false)
              reload()
            }}
          />
        )}
        {del && (
          <PolicyDeleteDialog
            escalationPolicyID={p.id}
            onClose={() => setDel(false)}
          />
        )}
        {addStep && (
          <StepDialog
            policyID={p.id}
            stepNumber={steps.length + 1}
            last
            repeat={p.repeat}
            onClose={() => {
              setAddStep(false)
              reload()
            }}
          />
        )}
        {editStep && (
          <StepDialog
            policyID={p.id}
            stepID={editStep}
            actions={steps.find((st) => st.id === editStep)?.actions}
            delayMinutes={steps.find((st) => st.id === editStep)?.delayMinutes}
            channels={sends[editStep]}
            stepNumber={steps.findIndex((st) => st.id === editStep) + 1}
            last={steps[steps.length - 1]?.id === editStep}
            repeat={p.repeat}
            onClose={() => {
              setEditStep(undefined)
              reload()
            }}
          />
        )}
        {delStep && (
          <PolicyStepDeleteDialog
            escalationPolicyID={p.id}
            stepID={delStep}
            onClose={() => {
              setDelStep(undefined)
              reload()
            }}
          />
        )}
      </Suspense>

      <Box
        sx={{
          p: 2,
          mb: 2,
          borderRadius: '14px',
          bgcolor: c.soft,
          border: `1px solid ${c.line}`,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <IconTile tone='muted'>
          {callsPeople ? <PhoneInTalk /> : <NotificationsActive />}
        </IconTile>
        <Typography sx={{ fontSize: 15, color: c.ink }}>
          {sentence(steps, p.repeat)}
        </Typography>
      </Box>
      {upd.error && (
        <MUIAlert severity='error' sx={{ mb: 2 }}>
          {upd.error.message}
        </MUIAlert>
      )}

      <Stack spacing={2}>
        <Panel
          title='Steps'
          subtitle='Each step notifies its people and channels. If nobody acknowledges in time, the next step starts.'
        >
          {steps.map((st, i) => {
            const start = at
            at += st.delayMinutes
            const last = i === steps.length - 1
            return (
              <React.Fragment key={st.id}>
                <Box
                  draggable={gripped === st.id}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', st.id)
                    setDrag(i)
                  }}
                  onDragEnd={() => {
                    setDrag(null)
                    setOver(null)
                    setGripped(null)
                  }}
                  onDragOver={(e) => {
                    if (drag === null) return
                    e.preventDefault()
                    const r = e.currentTarget.getBoundingClientRect()
                    const after = e.clientY > r.top + r.height / 2
                    if (over?.i !== i || over.after !== after)
                      setOver({ i, after })
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    drop()
                  }}
                  sx={{
                    position: 'relative',
                    opacity: drag === i ? 0.45 : 1,
                  }}
                >
                  {over?.i === i && drag !== null && drag !== i && (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        height: 3,
                        borderRadius: 2,
                        bgcolor: c.ink,
                        zIndex: 1,
                        ...(over.after ? { bottom: -10 } : { top: -10 }),
                      }}
                    />
                  )}
                  <StepCard
                    n={i + 1}
                    heading={
                      i === 0
                        ? 'Right away'
                        : `After ${start} min, if nobody acknowledged`
                    }
                    actions={st.actions}
                    first={i === 0}
                    last={last}
                    busy={upd.fetching}
                    onEdit={() => setEditStep(st.id)}
                    onMove={(d) => move(i, d)}
                    onDelete={() => setDelStep(st.id)}
                    sends={sends[st.id]}
                    grip={
                      steps.length > 1 ? (
                        <Box
                          title='Drag to move this step'
                          aria-hidden
                          onMouseDown={() => setGripped(st.id)}
                          onMouseUp={() => setGripped(null)}
                          sx={{
                            display: 'grid',
                            placeItems: 'center',
                            ml: -1,
                            mr: -0.75,
                            color: c.muted,
                            cursor: drag === null ? 'grab' : 'grabbing',
                            '&:hover': { color: c.ink },
                            '& svg': { fontSize: 20 },
                          }}
                        >
                          <DragIndicator />
                        </Box>
                      ) : undefined
                    }
                  />
                </Box>
                {!last && (
                  <Connector>
                    <Schedule />
                    Wait {st.delayMinutes} min for an acknowledgement
                  </Connector>
                )}
              </React.Fragment>
            )
          })}

          {steps.length > 0 && <Connector />}
          <ButtonBase
            onClick={() => setAddStep(true)}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              gap: 1.5,
              p: 2,
              borderRadius: '14px',
              border: `1.5px dashed ${c.line}`,
              color: c.ink,
              textAlign: 'left',
              '&:hover': { borderColor: c.muted, bgcolor: c.hover },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                border: `1.5px dashed ${c.muted}`,
                flexShrink: 0,
                '& svg': { fontSize: 18 },
              }}
            >
              <Add />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Add step {steps.length + 1}
              </Typography>
              <Typography sx={{ fontSize: 13, color: c.muted }}>
                {steps.length
                  ? 'Who to notify next if nobody acknowledges.'
                  : 'Who to notify when an alert comes in.'}
              </Typography>
            </Box>
          </ButtonBase>

          {steps.length > 0 && (
            <React.Fragment>
              <Connector>
                {p.repeat > 0 && (
                  <React.Fragment>
                    <Schedule />
                    Wait {steps[steps.length - 1].delayMinutes} min
                  </React.Fragment>
                )}
              </Connector>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  pl: 2,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: c.soft,
                    border: `1px solid ${c.line}`,
                    color: c.muted,
                    flexShrink: 0,
                    '& svg': { fontSize: 16 },
                  }}
                >
                  {p.repeat > 0 ? <Replay /> : <Check />}
                </Box>
                <Typography sx={{ fontWeight: 600 }}>
                  If still nobody acknowledged
                </Typography>
                <RepeatChoice
                  value={p.repeat}
                  disabled={upd.fetching}
                  onChange={(n) => save({ repeat: n })}
                />
              </Box>
            </React.Fragment>
          )}
        </Panel>

        <Panel
          flush
          title='Used by'
          subtitle='Alerts from these services follow this policy.'
          action={
            services.length ? (
              <Button
                size='small'
                component={AppLink}
                to={`/escalation-policies/${p.id}/alerts`}
              >
                Their alerts
              </Button>
            ) : undefined
          }
        >
          {services.length === 0 && (
            <Empty
              title='Not used yet'
              text='Pick this policy on a service to send its alerts here.'
            />
          )}
          {services.map((s) => (
            <Row
              key={s.id}
              to={`/services/${s.id}`}
              title={s.name}
              meta={<SeverityChip name={s.name} />}
            />
          ))}
        </Panel>

        <Box>
          <Button
            color='error'
            startIcon={<Delete />}
            onClick={() => setDel(true)}
          >
            Delete policy
          </Button>
        </Box>
      </Stack>
    </Page>
  )
}
